/**
 * #37717 (Joe): a farm structure's age for the class and age rules is its age at the TERM START, the year of the term's
 * effective date, as the farm rater sends ClarionDoor (`home_age`, farm-utils getTermEffectiveYear). Measured against
 * today's year, a barn that rated as 25 on a term effective 2025-12-15 turned "26" on 2026-01-01 and blocked every
 * amendment of a term it was valid for.
 *
 * The term start comes from the form values: `global.farmEffectiveDate` on an offer (and any segment that carries it),
 * otherwise the Details tab's form-only `termEffectiveDate` (the policy's effective date, stripped from the payload by
 * sanitize.js). With neither (the quote form, before an effective date is chosen), today's year.
 *
 * CFM only (stakeholders: CFM on, Forreston off), behind `ageFarmStructuresAtTermStart`: only the CFM farm rater ages
 * at the term start.
 */
import { getTermEffectiveYear } from '@ourbranch/farm-utils';
import { appConfig } from 'brand-config';

/**
 * The year a structure's age is measured at for the term these form values describe. Only a brand whose farm rater ages
 * at the term start opts in (`ageFarmStructuresAtTermStart`, CFM); every other brand (Forreston, Oakwood) ages in
 * today's year, as its rater does.
 */
export const getTermStartYear = (values) =>
  appConfig?.featureFlags?.ageFarmStructuresAtTermStart
    ? getTermEffectiveYear({ effectiveDate: values?.termEffectiveDate, global: values?.global })
    : new Date().getFullYear();

/**
 * A structure's age in whole years at the term start, or null without a usable year built. A structure built after the
 * term started (added by an endorsement) is 0, never negative.
 */
export const getBuildingAgeAtTermStart = (yearBuilt, values) => {
  const year = Number.parseInt(yearBuilt, 10);
  return Number.isFinite(year) && year > 0 ? Math.max(0, getTermStartYear(values) - year) : null;
};
